/**
 * Social Feed API Service
 * Base URL: /admin/v1/hospital/social
 */

import axios from "../utils/index";
import {
  CreatePostRequest,
  PostResponse,
  PostListResponse,
  MediaUploadResponse,
  LikeResponse,
  DeleteResponse,
} from "../interface/post";
import {
  CreateCommentRequest,
  ReplyCommentRequest,
  CommentResponse,
  CommentListResponse,
} from "../interface/comment";

const BASE_URL = "/admin/v1/hospital/social";

const socialApi = {
  /**
   * Create a new hospital post
   * POST /admin/v1/hospital/social/posts
   */
  createPost({
    params,
    callback = (() => {}) as any,
  }: {
    params: CreatePostRequest;
    callback?: (res: any) => void;
  }) {
    axios
      .post(`${BASE_URL}/posts`, params)
      .then((res) => {
        callback(res);
      })
      .catch((err) => {
        callback(err);
      });
  },

  /**
   * Get hospital's own posts with pagination
   * GET /admin/v1/hospital/social/posts?page=0&size=20
   */
  getHospitalPosts({
    params = { page: 0, size: 20 },
    callback = (() => {}) as any,
    cancelToken = null as any,
  }: {
    params?: { page?: number; size?: number };
    callback?: (res: any) => void;
    cancelToken?: any;
  }) {
    axios
      .get(`${BASE_URL}/posts`, {
        params: {
          page: params.page || 0,
          size: params.size || 20,
        },
        cancelToken,
      })
      .then((res) => {
        callback(res);
      })
      .catch((err) => {
        callback(err);
      });
  },

  /**
   * Public community feed (doctors + hospitals)
   * GET /admin/v1/hospital/social/feed?page=0&size=20
   */
  getCommunityFeed({
    params = { page: 0, size: 20 },
    callback = (() => {}) as any,
    cancelToken = null as any,
  }: {
    params?: { page?: number; size?: number };
    callback?: (res: any) => void;
    cancelToken?: any;
  }) {
    axios
      .get(`${BASE_URL}/feed`, {
        params: {
          page: params.page || 0,
          size: params.size || 20,
        },
        cancelToken,
      })
      .then((res) => {
        callback(res);
      })
      .catch((err) => {
        callback(err);
      });
  },

  /**
   * Public posts by a doctor (social profile)
   * GET /admin/v1/hospital/social/users/{userId}/posts
   */
  getDoctorPublicPosts({
    userId,
    params = { page: 0, size: 20 },
    callback = (() => {}) as any,
    cancelToken = null as any,
  }: {
    userId: string;
    params?: { page?: number; size?: number };
    callback?: (res: any) => void;
    cancelToken?: any;
  }) {
    axios
      .get(`${BASE_URL}/users/${userId}/posts`, {
        params: {
          page: params.page || 0,
          size: params.size || 20,
        },
        cancelToken,
      })
      .then((res) => {
        callback(res);
      })
      .catch((err) => {
        callback(err);
      });
  },

  /**
   * Upload media for post (image, video, document)
   * POST /admin/v1/hospital/social/posts/media
   */
  uploadMedia({
    params,
    callback = (() => {}) as any,
    onProgress = (() => {}) as any,
  }: {
    params: { file: File; type: "image" | "video" | "document" };
    callback?: (res: any) => void;
    onProgress?: (progressEvent: any) => void;
  }) {
    const formData = new FormData();
    formData.append("file", params.file);
    formData.append("type", params.type);

    axios
      .post(`${BASE_URL}/posts/media`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: onProgress,
      })
      .then((res) => {
        callback(res);
      })
      .catch((err) => {
        callback(err);
      });
  },

  /**
   * Add a comment to a post
   * POST /admin/v1/hospital/social/posts/{postId}/comments
   */
  addComment({
    postId,
    params,
    callback = (() => {}) as any,
  }: {
    postId: string;
    params: CreateCommentRequest;
    callback?: (res: any) => void;
  }) {
    axios
      .post(`${BASE_URL}/posts/${postId}/comments`, params)
      .then((res) => {
        callback(res);
      })
      .catch((err) => {
        callback(err);
      });
  },

  /**
   * Reply to a comment
   * POST /admin/v1/hospital/social/comments/{commentId}/reply
   */
  replyToComment({
    commentId,
    params,
    callback = (() => {}) as any,
  }: {
    commentId: string;
    params: ReplyCommentRequest;
    callback?: (res: any) => void;
  }) {
    axios
      .post(`${BASE_URL}/comments/${commentId}/reply`, params)
      .then((res) => {
        callback(res);
      })
      .catch((err) => {
        callback(err);
      });
  },

  /**
   * Like or unlike a post
   * POST /admin/v1/hospital/social/posts/{postId}/like
   */
  togglePostLike({
    postId,
    callback = (() => {}) as any,
  }: {
    postId: string;
    callback?: (res: any) => void;
  }) {
    axios
      .post(`${BASE_URL}/posts/${postId}/like`)
      .then((res) => {
        callback(res);
      })
      .catch((err) => {
        callback(err);
      });
  },

  /**
   * Like or unlike a comment
   * POST /admin/v1/hospital/social/comments/{commentId}/like
   */
  toggleCommentLike({
    commentId,
    callback = (() => {}) as any,
  }: {
    commentId: string;
    callback?: (res: any) => void;
  }) {
    axios
      .post(`${BASE_URL}/comments/${commentId}/like`)
      .then((res) => {
        callback(res);
      })
      .catch((err) => {
        callback(err);
      });
  },

  /**
   * Get comments for a post
   * GET /admin/v1/hospital/social/posts/{postId}/comments?limit=50
   */
  getPostComments({
    postId,
    params = { limit: 50 },
    callback = (() => {}) as any,
    cancelToken = null as any,
  }: {
    postId: string;
    params?: { limit?: number };
    callback?: (res: any) => void;
    cancelToken?: any;
  }) {
    axios
      .get(`${BASE_URL}/posts/${postId}/comments`, {
        params: {
          limit: params.limit || 50,
        },
        cancelToken,
      })
      .then((res) => {
        callback(res);
      })
      .catch((err) => {
        callback(err);
      });
  },

  /**
   * Get replies for a comment
   * GET /admin/v1/hospital/social/comments/{commentId}/replies?limit=20
   */
  getCommentReplies({
    commentId,
    params = { limit: 20 },
    callback = (() => {}) as any,
    cancelToken = null as any,
  }: {
    commentId: string;
    params?: { limit?: number };
    callback?: (res: any) => void;
    cancelToken?: any;
  }) {
    axios
      .get(`${BASE_URL}/comments/${commentId}/replies`, {
        params: {
          limit: params.limit || 20,
        },
        cancelToken,
      })
      .then((res) => {
        callback(res);
      })
      .catch((err) => {
        callback(err);
      });
  },

  /**
   * Delete a hospital post
   * DELETE /admin/v1/hospital/social/posts/{postId}
   */
  deletePost({
    postId,
    callback = (() => {}) as any,
  }: {
    postId: string;
    callback?: (res: any) => void;
  }) {
    axios
      .delete(`${BASE_URL}/posts/${postId}`)
      .then((res) => {
        callback(res);
      })
      .catch((err) => {
        callback(err);
      });
  },

  /**
   * Smart social feed — posts, reposts, and activity cards chronologically
   */
  getSmartFeed({
    params = { offset: 0, size: 20 },
    callback = (() => {}) as any,
  }: {
    params?: { offset?: number; size?: number };
    callback?: (res: any) => void;
  }) {
    axios
      .get(`${BASE_URL}/smart-feed`, {
        params: {
          offset: params.offset || 0,
          size: params.size || 20,
        },
      })
      .then((res) => callback(res))
      .catch((err) => callback(err));
  },

  /**
   * Social-only notifications (likes, comments, reposts, mentions)
   */
  getSocialNotifications({
    params = { offset: 0, size: 30 },
    callback = (() => {}) as any,
  }: {
    params?: { offset?: number; size?: number };
    callback?: (res: any) => void;
  }) {
    axios
      .get(`${BASE_URL}/notifications`, {
        params: {
          offset: params.offset || 0,
          size: params.size || 30,
        },
      })
      .then((res) => callback(res))
      .catch((err) => callback(err));
  },

  markSocialNotificationsRead({
    params,
    callback = (() => {}) as any,
  }: {
    params: { notificationIds?: number[]; markAll?: boolean };
    callback?: (res: any) => void;
  }) {
    axios
      .post(`${BASE_URL}/notifications/read`, params)
      .then((res) => callback(res))
      .catch((err) => callback(err));
  },

  /**
   * Get users who liked a post
   * GET /admin/v1/hospital/social/posts/{postId}/likes
   */
  getPostLikes({
    postId,
    params = { page: 0, size: 30 },
    callback = (() => {}) as any,
  }: {
    postId: string;
    params?: { page?: number; size?: number };
    callback?: (res: any) => void;
  }) {
    axios
      .get(`${BASE_URL}/posts/${postId}/likes`, {
        params: {
          page: params.page || 0,
          size: params.size || 30,
        },
      })
      .then((res) => callback(res))
      .catch((err) => callback(err));
  },

  /**
   * Get users who reposted a post
   * GET /admin/v1/hospital/social/posts/{postId}/shares
   */
  getPostShares({
    postId,
    params = { page: 0, size: 30 },
    callback = (() => {}) as any,
  }: {
    postId: string;
    params?: { page?: number; size?: number };
    callback?: (res: any) => void;
  }) {
    axios
      .get(`${BASE_URL}/posts/${postId}/shares`, {
        params: {
          page: params.page || 0,
          size: params.size || 30,
        },
      })
      .then((res) => callback(res))
      .catch((err) => callback(err));
  },

  /**
   * Repost / share a post to hospital profile
   * POST /admin/v1/hospital/social/posts/{postId}/share
   */
  sharePost({
    postId,
    params,
    callback = (() => {}) as any,
  }: {
    postId: string;
    params?: { message?: string; shareType?: "internal" | "external"; platform?: string };
    callback?: (res: any) => void;
  }) {
    axios
      .post(`${BASE_URL}/posts/${postId}/share`, {
        message: params?.message,
        shareType: params?.shareType || "internal",
        platform: params?.platform,
      })
      .then((res) => callback(res))
      .catch((err) => callback(err));
  },

  /**
   * Remove a repost from hospital profile
   * DELETE /admin/v1/hospital/social/shares/{shareId}
   */
  deleteRepost({
    shareId,
    callback = (() => {}) as any,
  }: {
    shareId: string;
    callback?: (res: any) => void;
  }) {
    axios
      .delete(`${BASE_URL}/shares/${shareId}`)
      .then((res) => callback(res))
      .catch((err) => callback(err));
  },

  /**
   * Mention autocomplete candidates for a post thread
   * GET /admin/v1/hospital/social/mentions/candidates
   */
  getMentionCandidates({
    postId,
    callback = (() => {}) as any,
  }: {
    postId?: string;
    callback?: (res: any) => void;
  }) {
    axios
      .get(`${BASE_URL}/mentions/candidates`, { params: { postId } })
      .then((res) => callback(res))
      .catch((err) => callback(err));
  },

  /**
   * Recent @mentions of this hospital
   * GET /admin/v1/hospital/social/mentions/recent
   */
  getRecentMentions({
    params = { limit: 20 },
    callback = (() => {}) as any,
  }: {
    params?: { limit?: number };
    callback?: (res: any) => void;
  }) {
    axios
      .get(`${BASE_URL}/mentions/recent`, { params: { limit: params.limit || 20 } })
      .then((res) => callback(res))
      .catch((err) => callback(err));
  },

  /**
   * Get a single post by ID
   * GET /admin/v1/hospital/social/posts/{postId}
   */
  getPostById({
    postId,
    callback = (() => {}) as any,
    cancelToken = null as any,
  }: {
    postId: string;
    callback?: (res: any) => void;
    cancelToken?: any;
  }) {
    axios
      .get(`${BASE_URL}/posts/${postId}`, { cancelToken })
      .then((res) => {
        callback(res);
      })
      .catch((err) => {
        callback(err);
      });
  },

  /**
   * Update an existing post
   * PUT /admin/v1/hospital/social/posts/{postId}
   */
  updatePost({
    postId,
    params,
    callback = (() => {}) as any,
  }: {
    postId: string;
    params: Partial<CreatePostRequest>;
    callback?: (res: any) => void;
  }) {
    axios
      .put(`${BASE_URL}/posts/${postId}`, params)
      .then((res) => {
        callback(res);
      })
      .catch((err) => {
        callback(err);
      });
  },

  /**
   * Get this hospital's active story slides (24h)
   * GET /admin/v1/hospital/social/stories
   */
  getHospitalStories({
    callback = (() => {}) as any,
  }: {
    callback?: (res: any) => void;
  }) {
    axios
      .get(`${BASE_URL}/stories`)
      .then((res) => callback(res))
      .catch((err) => callback(err));
  },

  /**
   * Doctor story rings visible to this hospital
   * GET /admin/v1/hospital/social/stories/rings
   */
  getHospitalStoryRings({
    limit = 30,
    callback = (() => {}) as any,
  }: {
    limit?: number;
    callback?: (res: any) => void;
  }) {
    axios
      .get(`${BASE_URL}/stories/rings`, { params: { limit } })
      .then((res) => callback(res))
      .catch((err) => callback(err));
  },

  /**
   * Publish a hospital story slide (24h)
   * POST /admin/v1/hospital/social/stories
   */
  createStory({
    params,
    callback = (() => {}) as any,
  }: {
    params: {
      mediaUrl: string;
      mediaType?: string;
      caption?: string;
      thumbnailUrl?: string;
    };
    callback?: (res: any) => void;
  }) {
    axios
      .post(`${BASE_URL}/stories`, params)
      .then((res) => callback(res))
      .catch((err) => callback(err));
  },

  /**
   * Delete a hospital story slide
   * DELETE /admin/v1/hospital/social/stories/{itemId}
   */
  deleteStory({
    itemId,
    callback = (() => {}) as any,
  }: {
    itemId: string;
    callback?: (res: any) => void;
  }) {
    axios
      .delete(`${BASE_URL}/stories/${itemId}`)
      .then((res) => callback(res))
      .catch((err) => callback(err));
  },

  markStorySeen({
    authorType,
    authorId,
    lastItemId,
    callback = (() => {}) as any,
  }: {
    authorType: string;
    authorId: string;
    lastItemId?: string;
    callback?: (res: any) => void;
  }) {
    axios
      .post(`${BASE_URL}/stories/${authorType}/${authorId}/seen`, {
        lastItemId,
      })
      .then((res) => callback(res))
      .catch((err) => callback(err));
  },

  recordStoryView({
    itemId,
    callback = (() => {}) as any,
  }: {
    itemId: string;
    callback?: (res: any) => void;
  }) {
    axios
      .post(`${BASE_URL}/stories/${itemId}/view`)
      .then((res) => callback(res))
      .catch((err) => callback(err));
  },

  toggleStoryLike({
    itemId,
    callback = (() => {}) as any,
  }: {
    itemId: string;
    callback?: (res: any) => void;
  }) {
    axios
      .post(`${BASE_URL}/stories/${itemId}/like`)
      .then((res) => callback(res))
      .catch((err) => callback(err));
  },

  getStoryEngagement({
    itemId,
    callback = (() => {}) as any,
  }: {
    itemId: string;
    callback?: (res: any) => void;
  }) {
    axios
      .get(`${BASE_URL}/stories/${itemId}/engagement`)
      .then((res) => callback(res))
      .catch((err) => callback(err));
  },

  getStoryComments({
    itemId,
    offset = 0,
    size = 30,
    callback = (() => {}) as any,
  }: {
    itemId: string;
    offset?: number;
    size?: number;
    callback?: (res: any) => void;
  }) {
    axios
      .get(`${BASE_URL}/stories/${itemId}/comments`, { params: { offset, size } })
      .then((res) => callback(res))
      .catch((err) => callback(err));
  },

  addStoryComment({
    itemId,
    commentText,
    callback = (() => {}) as any,
  }: {
    itemId: string;
    commentText: string;
    callback?: (res: any) => void;
  }) {
    axios
      .post(`${BASE_URL}/stories/${itemId}/comments`, { commentText })
      .then((res) => callback(res))
      .catch((err) => callback(err));
  },

  /**
   * Fetch rich link preview metadata for a pasted URL.
   * GET /admin/v1/hospital/social/url-preview?url=...
   */
  fetchUrlPreview({
    url,
    callback = (() => {}) as any,
  }: {
    url: string;
    callback?: (res: any) => void;
  }) {
    axios
      .get(`${BASE_URL}/url-preview`, { params: { url } })
      .then((res) => callback(res))
      .catch((err) => callback(err));
  },
};

export default socialApi;
